<template>
  <div
    ref="recordHeader"
    :class="$style.recordHeader"
    v-observe-visibility="observerVisibility.getOptions('half')"
  >
    <record-header-info
      :headerData="recordData"
      :is-mobile-only="isMobileOnly"
      :region-id="regionId"
      :is-header-link="hasHeaderLink"
      @rubricClick="sendRubricReachGoal($event)"
      @formatClick="sendFormatReachGoal($event)"
      @themeClick="sendThemeReachGoal($event)"
      @dateClick="sendDateReachGoal()"
    />

    <mobile-app-invite
      v-if="hasMobileAppInvite"
      :site-name="siteName"
      :app-url="appUrl"
      :region-id="regionId"
      @appInviteMounted="sendInviteAppMountedReachGoal"
      @appInviteClicked="sendInviteAppClickedReachGoal"
    />

    <record-characteristics
      :is-actions-shown="isActionsShown"
      :comments="recordData.commentsCount"
      :comments-link="urlComments"
      :is-comments-allowed="recordData.isCommentsAllowed"
      :record-title="recordTitle"
      :record-id="recordId"
      :has-record-content="hasRecordContent"
      :is-in-bookmarks="isInBookmarks"
      @clickedBookmark="changeBookmarkStatus()"
      @clickedCommentsLink="sendCommentsReachGoal()"
      @viewShareBlock="onViewShareBlock()"
      @shareBlock="onShareBlock($event)"
      @clickCommentsInStickyPanel="sendCommentsInStickyPanelRG"
      @clickedBookmarkInStickyPanel="sendBookmarkInStickyPanelRG"
      @shareRecordInStickyPanel="sendShareRecordInStickyPanelRG($event)"
      @linkCopiedInStickyPanel="sendLinkCopiedInStickyPanelRG"
    >
      <!-- TODO: вернуть блок озвучки REGIONNEWS-30193 -->
      <!-- <retell
        slot="retell"
        v-if="recordData.voicedTextUrl && recordData.voicedTextUrl.length > 0"
        :url="recordData.voicedTextUrl"
        :regionId="regionId"
        @playerEvents="sendVoicedReachGoal($event)"
      /> -->
    </record-characteristics>

    <content-blocks-list v-if="isShowContent" :data="recordData.text" />

    <bookmark-error-modal
      v-if="isBookmarkErrorModalRendered"
      v-model="isBookmarkErrorModalOpened"
    />
    <something-wrong-modal
      v-if="isSomethingWrongModalRendered"
      v-model="isSomethingWrongModalOpen"
    />

    <auth-and-register-modal v-if="isAuthModalRendered" v-model="isAuthModalOpen" />
  </div>
</template>

<script>
import { getDomainByRegionId } from '@jtnews/shared/seedwork/bff/domain';
import { guardUnspecified, guardEmptyString } from '@portal/utils/util-guards';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ObserverVisibility, Retell } from '@jtnews/shared';

import ContentBlocksList from '../../../../features/record/containers/content-blocks-list';

import { StoreMixin } from '../../../mixins';
import RecordCharacteristics from '../record-characteristics';
import { RecordHeaderInfo } from '../record-header-info';
import { MobileAppInvite } from '../../mobile-app-invite';

// eslint-disable-next-line @typescript-eslint/naming-convention
const SomethingWrongModal = () =>
  import(/* webpackChunkName: "something-wrong-modal" */ '@jtnews/layout/modals').then(
    m => m.SomethingWrongModal
  );

// eslint-disable-next-line @typescript-eslint/naming-convention
const BookmarkErrorModal = () =>
  import(/* webpackChunkName: "bookmark-error-modal" */ '@jtnews/layout/modals').then(
    m => m.BookmarkErrorModal
  );

// eslint-disable-next-line @typescript-eslint/naming-convention
const AuthAndRegisterModal = () =>
  import(/* webpackChunkName: "auth-and-register-modal" */ '@jtnews/layout/modals').then(
    m => m.AuthAndRegisterModal
  );

export default {
  name: 'RecordHeader',
  components: {
    RecordHeaderInfo,
    RecordCharacteristics,
    ContentBlocksList,
    MobileAppInvite,
    BookmarkErrorModal,
    SomethingWrongModal,
    AuthAndRegisterModal
    // TODO: вернуть блок озвучки REGIONNEWS-30193
    // Retell
  },
  mixins: [StoreMixin],
  props: {
    isShowContent: {
      type: Boolean,
      default: false
    },
    isActionsShown: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      destroy$: new Subject(),
      headerImage: {},
      figureWidth: 0,
      figureHeight: 0,
      observerVisibility: new ObserverVisibility('half'),
      isBookmarkProcessing: false,
      isBookmarkErrorModalOpened: false,
      isBookmarkErrorModalRendered: false,
      isSomethingWrongModalOpen: false,
      isSomethingWrongModalRendered: false,
      isAuthModalOpen: false,
      isAuthModalRendered: false
    };
  },
  computed: {
    regionId() {
      return this.store.regionId;
    },
    isMobile() {
      return this.store.deviceInfo.isMobile;
    },
    isMobileOnly() {
      return this.isMobile && !this.store.deviceInfo.isTablet;
    },
    recordBlock() {
      return this.store.layoutModule.record;
    },
    recordData() {
      return this.recordBlock?.data ?? {};
    },
    hasRecordContent() {
      return this.recordData?.text?.length > 0;
    },
    urlComments() {
      if (this.recordData.urls && this.recordData.urls.urlComments) {
        return this.recordData.urls.urlComments;
      }

      return '';
    },
    recordTitle() {
      if (this.recordData.header) {
        return this.recordData.header;
      }

      return '';
    },
    hasHeaderLink() {
      return this.$route.name !== 'record';
    },
    recordId() {
      return this.recordData.id;
    },
    userInfo() {
      return this.store.commonModule.userInfo || null;
    },
    isInBookmarks() {
      return this.store.userModule.recordsWithBookmark.includes(this.recordId);
    },
    isBookmarkError() {
      return this.store.userModule.isBookmarkLimitError;
    },
    isMobileAppInvite() {
      return (
        guardUnspecified(this.recordBlock.params.appUrl) &&
        guardEmptyString(this.recordBlock.params.appUrl)
      );
    },
    loyaltySegment() {
      if (guardUnspecified(this.store.commonModule.loyaltySegment)) {
        return this.store.commonModule.loyaltySegment.toLowerCase();
      }
      return '';
    },
    isSuitableLoyaltySegment() {
      return this.loyaltySegment === 'disloyal' || this.loyaltySegment === 'average';
    },
    hasMobileAppInvite() {
      return this.isMobile && this.isMobileAppInvite && this.isSuitableLoyaltySegment;
    },
    siteName() {
      return getDomainByRegionId(this.store.regionId);
    },
    appUrl() {
      if (guardUnspecified(this.loyaltySegment)) {
        return `${this.recordBlock.params.appUrl}?segment=${this.loyaltySegment}`;
      }

      return this.recordBlock.params.appUrl;
    }
  },
  mounted() {
    this.observerVisibility.event.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state.hook === 'half' && state.value) {
        this.sendHeadReachGoal('Просмотр', 'viewHeadArt');
        this.sendNewFormatReachGoal('ViewArticleHeader', 'Шапка', 'Просмотр');
      }
    });
  },
  methods: {
    sendClickAnalytics(type) {
      this.sendNewFormatReachGoal(
        'ClickArticleHeader',
        'Шапка',
        'Клик',
        this.recordId,
        type
      );
    },
    sendNewFormatReachGoal(goalName, blockType, actionType, prop1, prop2) {
      this.store.analyticsModule.sendNewFormatReachGoal({
        goalName,
        blockType,
        actionType,
        prop1,
        prop2
      });
    },
    async changeBookmarkStatus() {
      if (this.isBookmarkProcessing === true) {
        return;
      }

      const bookmarkId = this.store.userModule.bookmarkIdsByRecord[this.recordId] || '';

      if (this.userInfo === null) {
        this.store.userModule.sendBookmarkReachGoal({
          recordId: this.recordId,
          bookmarkId,
          isAuthorized: false
        });
        this.sendClickAnalytics('В закладки');
        this.showAuthModal();
        return;
      }

      this.isBookmarkProcessing = true;

      try {
        await this.store.userModule.changeBookmarkStatus({
          regionId: this.regionId,
          profileId: this.userInfo.userId,
          recordId: this.recordId,
          bookmarkId,
          type: 'record'
        });

        this.store.userModule.sendBookmarkReachGoal({
          recordId: this.recordId,
          bookmarkId
        });
      } catch (error) {
        this.openModal();
      } finally {
        this.isBookmarkProcessing = false;
      }
    },
    openModal() {
      if (this.isBookmarkError) {
        this.showBookmarkErrorModal();
        return;
      }

      this.openErrorModal();
    },
    openBookmarkErrorModal() {
      this.isBookmarkErrorModalOpened = true;
      this.isBookmarkErrorModalRendered = true;
    },
    openErrorModal() {
      this.isSomethingWrongModalOpen = true;
      this.isSomethingWrongModalRendered = true;
    },
    showAuthModal() {
      this.isAuthModalRendered = true;
      this.isAuthModalOpen = true;
    },
    sendRubricReachGoal(rubric) {
      this.sendHeadReachGoal(`Клик Рубрика: ${rubric}`, 'clickHeadRub');
      this.sendClickAnalytics(`Рубрика ${rubric}`);
    },
    sendFormatReachGoal(format) {
      this.sendClickAnalytics(`Формат ${format}`);
      this.sendHeadReachGoal(`Клик Формат: ${format}`, 'clickHeadFormat');
    },
    sendThemeReachGoal(theme) {
      this.sendClickAnalytics(`Тема ${theme}`);
      this.sendHeadReachGoal(`Клик Тема: ${theme}`, 'clickHeadTheme');
    },
    sendDateReachGoal() {
      this.sendClickAnalytics(`Дата`);
      this.sendHeadReachGoal('Клик Дата', 'clickHeadDate');
    },
    sendCommentsReachGoal() {
      this.sendHeadReachGoal('Клик Обсудить: Иконка', 'clickHeadDiscussIco');
      const title =
        this.recordData.commentsCount === 0 ? 'Написать комментарий' : 'Комментарии';
      this.sendClickAnalytics(title);
    },
    onViewShareBlock() {
      this.sendShareReachGoal('Просмотр', 'viewShareBlockTop');
    },
    onShareBlock(socialName) {
      this.sendShareReachGoal(`Клик на ${socialName}`, 'clickShareBlockTop');
      this.sendClickAnalytics(`Поделиться ${socialName}`);
    },
    sendShareReachGoal(valueName, goalName) {
      this.sendNewReachGoal('Шаринг(вверху)', [valueName], goalName);
    },
    sendHeadReachGoal(valueName, goalName) {
      this.sendNewReachGoal('Шапка материала', { [valueName]: this.recordId }, goalName);
    },
    sendVoicedReachGoal({ valueName, goalName }) {
      this.sendNewReachGoal('Озвучка текста', { [valueName]: this.recordId }, goalName);
      this.sendClickAnalytics(`Озвучка`);
    },
    sendCommentsInStickyPanelRG() {
      this.sendNewReachGoal('Залипающая плашка', 'Клик комментарии', 'clickcomment');
    },
    sendBookmarkInStickyPanelRG() {
      this.sendNewReachGoal(
        'Залипающая плашка',
        'Клик добавить избранное',
        'clickbookmark'
      );
    },
    sendShareRecordInStickyPanelRG(socialName) {
      this.sendNewReachGoal(
        'Залипающая плашка',
        { 'Клик шаринг': socialName },
        'clicksharing'
      );
    },
    sendNewReachGoal(fieldType, valueName, goalName) {
      const blockType = 'Центральная колонка';
      this.store.analyticsModule.sendNewReachGoal({
        blockType,
        fieldType,
        valueName,
        goalName,
        willDeprecate: true
      });
    },
    sendLinkCopiedInStickyPanelRG() {
      this.sendNewReachGoal(
        'Залипающая плашка',
        { 'Клик шаринг': 'Копировать ссылку' },
        'clicksharing'
      );
    },
    sendInviteAppClickedReachGoal() {
      this.sendNewReachGoal('Приглашение в приложение', 'Клик', 'AppInviteClick');
    },
    sendInviteAppMountedReachGoal() {
      this.sendNewReachGoal('Приглашение в приложение', 'Просмотр', 'AppInviteView');
    }
  },
  destroyed() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
};
</script>

<style lang="scss" module>
@import 'styles';

.recordHeader {
  box-sizing: border-box;
  width: 100%;

  background-color: $color-surface;

  @include on-res($desktop) {
    border-right: none;
  }
  @include on-res($mobile) {
    height: auto;
  }
}
</style>
